import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { Project } from "../../types";
import { NAVIGATION, ROLES } from "../../constants";
import { useProjects } from "../../hooks/useProjects";
import { useNavigationState } from "../../hooks/useNavigationState";
import { formatDate } from "../../lib/formatDate";
import DataTable from "../../components/tables/DataTable";
import FilesColumn from "../../components/files/FilesColumn";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";
import { useAuth } from "../../contexts/AuthContext";

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { state: navState, updateState } = useNavigationState();
  const [tabValue, setTabValue] = useState(navState.tab || 0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const isAdmin = user?.role === ROLES.ADMIN;

  // Restore navigation state when component mounts
  useEffect(() => {
    if (navState.tab !== undefined) {
      setTabValue(navState.tab);
    }
  }, [navState.tab]);

  const { projects, isLoading, error, deleteProject, isDeleting } =
    useProjects();

  const activeProjects = projects.filter(
    (project: Project) => project.isActive,
  );
  const inactiveProjects = projects.filter(
    (project: Project) => !project.isActive,
  );
  const currentProjects = tabValue === 0 ? activeProjects : inactiveProjects;

  const getColumns = (isInactive: boolean): GridColDef<Project>[] => {
    const baseColumns: GridColDef<Project>[] = [
      {
        field: "id",
        headerName: "#",
        width: 15,
        filterable: false,
        sortable: true,
        valueFormatter: (value: number, row: Project) =>
          value ? currentProjects.indexOf(row) + 1 : 0,
      },
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        filterable: true,
        sortable: true,
      },
      {
        field: "description",
        headerName: "Description",
        flex: 1.5,
        filterable: true,
        sortable: true,
      },
      {
        field: "client",
        headerName: "Client",
        flex: 1,
        filterable: true,
        sortable: true,
        valueGetter: (value: any) => value?.name || "",
      },
      {
        field: "currency",
        headerName: "Currency",
        flex: 1,
        filterable: true,
        sortable: true,
      },
      {
        field: "startDate",
        headerName: "Start Date",
        flex: 1,
        filterable: true,
        sortable: true,
        valueFormatter: (value: any) => (value ? formatDate(value) : ""),
      },
      {
        field: "endDate",
        headerName: "End Date",
        flex: 1,
        filterable: true,
        sortable: true,
        valueFormatter: (value: any) => (value ? formatDate(value) : ""),
      },
      {
        field: "files",
        headerName: "Files",
        width: 80,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <FilesColumn entityType="PROJECT" entityId={params.row.id} />
        ),
      },
    ];

    // Add actions column for admins
    if (isAdmin) {
      baseColumns.push({
        field: "actions",
        headerName: "Actions",
        width: 80,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Tooltip title="Delete Project">
            <IconButton
              size="small"
              color="error"
              onClick={(event: any) => {
                event.stopPropagation();
                setSelectedProject(params.row);
                setDeleteDialogOpen(true);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        ),
      });
    }

    // Add termination date column only for inactive projects
    if (isInactive) {
      baseColumns.splice(7, 0, {
        field: "terminationDate",
        headerName: "Termination Date",
        flex: 1,
        filterable: true,
        sortable: true,
        valueFormatter: (value: any) => (value ? formatDate(value) : ""),
      });
    }

    return baseColumns;
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    updateState({ tab: newValue, page: 0 }); // Reset page when changing tabs
  };

  const handleDeleteProject = () => {
    if (selectedProject) {
      deleteProject(selectedProject.id);
      setDeleteDialogOpen(false);
      setSelectedProject(null);
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedProject(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          Projects
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate(NAVIGATION.ADD_PROJECT)}
        >
          Add Project
        </Button>
      </Box>

      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label={`Active Projects (${activeProjects.length})`} />
        <Tab label={`Inactive Projects (${inactiveProjects.length})`} />
      </Tabs>

      {error instanceof Error && (
        <Typography color="error">{error.message}</Typography>
      )}

      <DataTable
        rows={currentProjects}
        columns={getColumns(tabValue === 1)}
        loading={isLoading}
        navigationPath={NAVIGATION.PROJECTS}
        initialPage={navState.page}
        onPageChange={(page) => updateState({ page })}
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeleteProject}
        title="Delete Project"
        itemName={selectedProject?.name || ""}
        itemType="project"
        loading={isDeleting}
      />
    </Box>
  );
};

export default Projects;
